import { useCallback, useEffect, useState } from "react";
import { listDocAssets } from "@/lib/document/docAssetsClient";
import type { DocAsset } from "@/lib/document/assetTypes";
import {
  getDocVersion,
  listDocDocuments,
  parseBlocksSnapshot,
  parseVariablesSnapshot,
} from "@/lib/document/documentsClient";
import type { DocDocumentListItem } from "@/lib/document/documentTypes";
import type { DocBlock } from "@/lib/document-builder/blocks/blockTypes";
import type { DocVariableSnapshot } from "@/lib/document-builder/versions/snapshotTypes";
import {
  buildInitialVariableValues,
  type DocVariableValues,
} from "@/lib/document-builder/variables/variableFill";

export function useDocumentLibrary(
  categoryId: string | null,
  options?: { includeDrafts?: boolean },
) {
  const includeDrafts = options?.includeDrafts ?? false;
  const [documents, setDocuments] = useState<DocDocumentListItem[]>([]);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(null);
  const [publishedBlocks, setPublishedBlocks] = useState<DocBlock[]>([]);
  const [publishedVersionId, setPublishedVersionId] = useState<string | null>(null);
  const [publishedVersionNumber, setPublishedVersionNumber] = useState<number | null>(null);
  const [publishedVariables, setPublishedVariables] = useState<DocVariableSnapshot[]>([]);
  const [fillValues, setFillValues] = useState<DocVariableValues>({});
  const [fillModalOpen, setFillModalOpen] = useState(false);
  const [exportPreviewOpen, setExportPreviewOpen] = useState(false);
  const [assets, setAssets] = useState<DocAsset[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await listDocDocuments({
      categoryId: categoryId ?? undefined,
      publishedOnly: !includeDrafts,
    });
    setLoading(false);
    if (!r.ok) {
      setError(r.error);
      setDocuments([]);
      return;
    }
    setDocuments(r.data.documents);
    setSelectedDocumentId((prev) => {
      if (prev && r.data.documents.some((d) => d.id === prev)) return prev;
      return null;
    });
  }, [categoryId, includeDrafts]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void (async () => {
      const r = await listDocAssets({ signMedia: true });
      if (r.ok) setAssets(r.data.assets);
    })();
  }, []);

  useEffect(() => {
    const doc = documents.find((d) => d.id === selectedDocumentId);
    if (!doc?.published_version_id) {
      setPublishedBlocks([]);
      setPublishedVersionId(null);
      setPublishedVersionNumber(null);
      setPublishedVariables([]);
      setFillValues({});
      setFillModalOpen(false);
      setExportPreviewOpen(false);
      return;
    }

    void (async () => {
      setViewerLoading(true);
      const r = await getDocVersion({ versionId: doc.published_version_id! });
      setViewerLoading(false);
      if (!r.ok) {
        setError(r.error);
        setPublishedBlocks([]);
        setPublishedVersionId(null);
        setPublishedVersionNumber(null);
        setPublishedVariables([]);
        return;
      }
      const variables = parseVariablesSnapshot(r.data.version.variables_snapshot);
      setPublishedBlocks(parseBlocksSnapshot(r.data.version.blocks_snapshot));
      setPublishedVersionId(r.data.version.id);
      setPublishedVersionNumber(r.data.version.version_number);
      setPublishedVariables(variables);
      setFillValues(buildInitialVariableValues(variables));
      setFillModalOpen(false);
      setExportPreviewOpen(false);
    })();
  }, [selectedDocumentId, documents]);

  const selectedDocument = documents.find((d) => d.id === selectedDocumentId) ?? null;

  function openFillModal() {
    if (!selectedDocument) return;
    setFillValues(buildInitialVariableValues(publishedVariables));
    setFillModalOpen(true);
  }

  function completeFill(values: DocVariableValues) {
    setFillValues(values);
    setFillModalOpen(false);
    setExportPreviewOpen(true);
  }

  function closeExportPreview() {
    setExportPreviewOpen(false);
  }

  return {
    documents,
    selectedDocumentId,
    setSelectedDocumentId,
    selectedDocument,
    publishedBlocks,
    publishedVersionId,
    publishedVersionNumber,
    publishedVariables,
    fillValues,
    fillModalOpen,
    exportPreviewOpen,
    assets,
    loading,
    viewerLoading,
    error,
    reload,
    openFillModal,
    completeFill,
    closeExportPreview,
    closeFillModal: () => setFillModalOpen(false),
  };
}
